import { useEffect, useRef, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { CellDisplay } from '../../fields/ui/read'
import type { FieldContext } from '../../fields/behavior/behavior'
import {
  columnLayout,
  gridColumns,
  type GridColumn,
  type GridFieldOptions,
} from '../../grid/columns'
import { AggregationFooter } from '../../grid/aggregation-footer'
import { usePublicViewAggregations } from '../../aggregations/queries'
import { GroupColumns } from '../../grid/group-columns'
import { groupSpans, groupsWidth, rowsEndingAGroup } from '../../grid/grouping'
import { useRowQuery, useRowWindow, useRowWindowStore } from '../../grid/use-grid'
import { rowAt, type RowWindow } from '../../rows/rowWindow'
import {
  rowMembershipSignature,
  type QueryableView,
  type RowSearch,
} from '../../rows/rowQuery'
import { cn } from '@/shared/lib/utils'

const GUTTER = 64

/**
 * A shared view, as somebody with the link sees it.
 *
 * The same row window engine the signed-in grid uses, pointed at the public
 * endpoint -- one scroll container, rows positioned absolutely inside a spacer
 * as tall as the table, frozen columns sticky. What it is not is `GridView`
 * with the editing switched off, and that is deliberate: everything that
 * component adds over showing rows *is* editing -- the portaled editor, the
 * field dialog, the context menu, clipboard writes, row creation, the column
 * width mutation, the realtime registration -- and none of it is merely unused
 * here. A publicly shared view has no view id and no table id to address any of
 * it with: the serialiser reports the slug as the id and a placeholder for the
 * table, on purpose, so that a link cannot be turned into knowledge of what
 * else the workspace holds. The shared parts are the ones already extracted:
 * the window, the column geometry, and the read-only cell renderers.
 */
export function PublicGrid({
  slug,
  view,
  fields,
  fieldOptions,
  rowHeight,
  search,
  fetchRows,
}: {
  slug: string
  view: QueryableView
  fields: readonly Field[]
  fieldOptions: Record<string, GridFieldOptions>
  rowHeight: number
  search: RowSearch
  fetchRows: Parameters<typeof useRowWindowStore>[0]
}) {
  const { t } = useTranslation()
  const store = useRowWindowStore(fetchRows)
  useRowQuery(store, view, search)
  const scroller = useRef<HTMLDivElement>(null)

  const count = useRowWindow(store, (state) => state.window.count)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)
  const groupByMetadata = useRowWindow(store, (state) => state.groupByMetadata)

  const columns = gridColumns(fields, fieldOptions)
  // The same geometry the signed-in grid uses: the group columns sit at the
  // left and move the origin everything else is measured from. The counts
  // beside them were asked for by the fetcher, which sends the group by
  // explicitly here because a public view's ordering is the reader's to send.
  const groupBys = view.group_bys ?? []
  const gutterLeft = groupsWidth(groupBys)

  // The owner's choice, shown and not offered: a reader of a shared view has
  // nothing here to change, so the footer is text rather than controls that
  // would have to refuse.
  const showsAggregations = Object.values(fieldOptions).some(
    (option) => (option.aggregation_raw_type ?? '') !== '',
  )
  const aggregations = usePublicViewAggregations(
    slug,
    rowMembershipSignature(view, search),
    search,
    { enabled: showsAggregations },
  )
  const { offsets, width } = columnLayout(columns, gutterLeft + GUTTER)

  useEffect(() => {
    const element = scroller.current
    if (element === null) return

    const report = () => {
      store.getState().setViewport({ height: element.clientHeight, rowHeight })
    }
    report()

    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [store, rowHeight])

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    store.getState().scrolled(event.currentTarget.scrollTop)
  }

  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  const groupLevels = groupSpans(groupBys, fields, groupByMetadata, visible, (index) =>
    rowAt(window, index),
  )
  const endsGroup = rowsEndingAGroup(groupBys, fields, visible, (index) =>
    rowAt(window, index),
  )

  return (
    <div
      ref={scroller}
      onScroll={onScroll}
      role="grid"
      aria-label={t('grid.label')}
      // One past the rows, because the header is a row too -- and these are the
      // only thing that can say where a row is in forty thousand when a few
      // dozen of them are in the document.
      aria-rowcount={count + 1}
      aria-colcount={columns.length + 1}
      // Read-only, and said so rather than left to be inferred from the absence
      // of controls: a grid a screen reader announces as editable is a grid
      // somebody will try to type into.
      aria-readonly
      key={slug}
      className="relative h-full overflow-auto"
    >
      {/* A column tall enough for the footer to reach the bottom of it, rather
          than as tall as the rows -- see the grid's own surface. */}
      <div className="flex min-h-full flex-col" style={{ width }}>
        <PublicHeader columns={columns} offsets={offsets} gutterLeft={gutterLeft} />
        <div className="relative shrink-0" style={{ height: count * rowHeight }}>
          {indexes.map((index) => (
            <PublicRow
              key={index}
              index={index}
              window={window}
              columns={columns}
              offsets={offsets}
              gutterLeft={gutterLeft}
              endsGroup={endsGroup}
              rowHeight={rowHeight}
            />
          ))}
          <GroupColumns
            groupBys={groupBys}
            fields={fields}
            levels={groupLevels}
            rowHeight={rowHeight}
            context={undefined}
          />
        </div>

        <AggregationFooter
          columns={columns}
          offsets={offsets}
          gutterLeft={gutterLeft}
          gutter={GUTTER}
          options={fieldOptions}
          aggregations={aggregations.data}
          context={undefined}
        />
      </div>
    </div>
  )
}

function PublicHeader({
  columns,
  offsets,
  gutterLeft,
}: {
  columns: readonly GridColumn[]
  offsets: readonly number[]
  /** How far in the gutter starts, which is the width of the group columns. */
  gutterLeft: number
}) {
  const { t } = useTranslation()

  return (
    <div
      role="row"
      aria-rowindex={1}
      className="bg-background sticky top-0 z-20 flex shrink-0 border-b"
      // The room the group columns take, left as padding so that the sticky
      // cells after it stick past them rather than against the container.
      style={{ paddingLeft: gutterLeft }}
    >
      <div
        role="columnheader"
        aria-colindex={1}
        className="bg-background sticky z-10 flex shrink-0 items-center border-r px-2 text-xs font-medium"
        style={{ width: GUTTER, left: gutterLeft }}
      >
        {t('grid.rowNumber')}
      </div>
      {columns.map((column, position) => (
        <div
          key={column.field.id}
          role="columnheader"
          aria-colindex={position + 2}
          className={cn(
            'flex shrink-0 items-center overflow-hidden border-r px-2 text-sm font-medium',
            column.frozen && 'bg-background sticky z-10',
          )}
          style={{
            width: column.width,
            ...(column.frozen ? { left: offsets[position] } : {}),
          }}
        >
          <span className="truncate">{column.field.name}</span>
        </div>
      ))}
    </div>
  )
}

function PublicRow({
  index,
  window: held,
  columns,
  offsets,
  gutterLeft,
  endsGroup,
  rowHeight,
  context,
}: {
  index: number
  window: RowWindow
  columns: readonly GridColumn[]
  offsets: readonly number[]
  gutterLeft: number
  endsGroup: ReadonlySet<number>
  rowHeight: number
  context?: FieldContext
}) {
  const row = rowAt(held, index)

  return (
    <div
      role="row"
      // The absolute index plus two: the header is row one, and the rows in the
      // document are a slice of the table rather than the whole of it.
      aria-rowindex={index + 2}
      className={cn(
        'absolute flex border-b',
        row !== undefined && endsGroup.has(row.id) && 'border-b-foreground/25',
      )}
      // The same room the header leaves; see the note there.
      style={{ top: index * rowHeight, height: rowHeight, paddingLeft: gutterLeft }}
    >
      <div
        role="gridcell"
        aria-colindex={1}
        className="bg-background text-muted-foreground sticky z-10 flex shrink-0 items-center border-r px-2 text-xs"
        style={{ width: GUTTER, left: gutterLeft }}
      >
        {index + 1}
      </div>
      {columns.map((column, position) => (
        <div
          key={column.field.id}
          role="gridcell"
          aria-colindex={position + 2}
          className={cn(
            'flex shrink-0 items-center overflow-hidden border-r px-2 text-sm',
            column.frozen && 'bg-background sticky z-10',
          )}
          style={{
            width: column.width,
            ...(column.frozen ? { left: offsets[position] } : {}),
          }}
        >
          {row === undefined ? (
            // Inside the viewport and outside the window: the reader has
            // scrolled past what is held and the fetch has not landed. A blank
            // cell of the right size is the honest thing to draw.
            <span className="bg-muted h-3 w-2/3 animate-pulse rounded" />
          ) : (
            <CellDisplay field={column.field} row={row} context={context} />
          )}
        </div>
      ))}
    </div>
  )
}
